import CustomAutocomplete, {
  TCustomAutocomplete,
} from "@/components/Form/InputFields/CustomAutocomplete";
import CustomListForAutocomplete from "@/components/List/CustomListForAutocomplete";
import { AutocompleteChangeReason, Chip } from "@mui/material";
import * as React from "react";
import { Control, useController, useForm, useWatch } from "react-hook-form";
type ListForm = {
  list: any[];
};
export default function CustomAutocompleteWithListButtons({
  primaryName,
  secondaryName,
  parentControl,
  itemList,
  handleEdit,
  onSearch,
  name,
  loading = false,
  ...rest
}: CustomAutocompleteWithListButtonsProps) {
  const {
    field: { value: includedValue = [], onChange: onIncludedChange },
  } = useController({
    control: parentControl,
    name: primaryName,
  });
  const {
    field: { value: excludedValue = [], onChange: onExcludedChange },
  } = useController({
    control: parentControl,
    name: secondaryName,
  });

  const { control, setValue } = useForm<ListForm>({
    defaultValues: {
      list: [],
    },
  });
  const listValues = useWatch({
    name: "list",
    control,
  });
  const includedValueWatch = useWatch({
    name: primaryName,
    control: parentControl,
  });
  const excludedValueWatch = useWatch({
    name: secondaryName,
    control: parentControl,
  });

  React.useEffect(() => {
    if (
      (!loading && includedValueWatch?.length) ||
      excludedValueWatch?.length
    ) {
      setValue("list", [
        ...(includedValueWatch ?? []),
        ...(excludedValueWatch ?? []),
      ]);
    }
  }, [includedValueWatch, excludedValueWatch, loading]);

  const handleClickIncluded = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number,
    onClick: (e: any) => void
  ) => {
    const value = itemList?.[index];

    const alreadyExcluded = checkIfValueExist(excludedValue, value.value);
    if (alreadyExcluded) {
      filterValue(excludedValue, value, onExcludedChange);
    }

    const alreadyIncluded = checkIfValueExist(includedValue, value.value);
    if (alreadyIncluded) {
      filterValue(includedValue, value, onIncludedChange);
    } else {
      onIncludedChange([...includedValue, value.value]);
    }

    if (alreadyExcluded) return;

    if (checkIfValueExist(listValues, value.value)) {
      setValue("list", customFilter(listValues, value.value));
    } else {
      setValue("list", [...listValues, value.value]);
    }
  };
  const onKeywordSearch = (value: string) => {
    onSearch(value, name);
  };
  const handleClickExcluded = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number,
    onClick: (e: any) => void
  ) => {
    const value = itemList?.[index];
    const alreadyIncluded = checkIfValueExist(includedValue, value.value);
    if (alreadyIncluded) {
      filterValue(includedValue, value, onIncludedChange);
    }

    const alreadyExcluded = checkIfValueExist(excludedValue, value.value);
    if (alreadyExcluded) {
      filterValue(excludedValue, value, onExcludedChange);
    } else {
      onExcludedChange([...excludedValue, value.value]);
    }

    if (alreadyIncluded) return;

    if (checkIfValueExist(listValues, value.value)) {
      setValue("list", customFilter(listValues, value.value));
    } else {
      setValue("list", [...listValues, value.value]);
    }
  };

  const handleDeleteTag = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    option: any,
    onDelete: (e: any) => void
  ) => {
    filterValue(includedValue, option, onIncludedChange);
    filterValue(excludedValue, option, onExcludedChange);
    onDelete(event);
  };

  const handleOnClear = (value: any, reason: AutocompleteChangeReason) => {
    if (reason === "clear") {
      if (includedValue?.length) onIncludedChange([]);
      if (excludedValue?.length) onExcludedChange([]);
    }
  };

  const onEdit = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number
  ) => {
    handleEdit?.(e, itemList?.[index]?.value);
  };

  return (
    <>
      <CustomAutocomplete<ListForm>
        {...rest}
        isCustomList={true}
        isMultiple={true}
        name="list"
        control={control}
        onSearch={onKeywordSearch}
        onChange={handleOnClear}
        itemList={itemList}
        virtualize={false}
        loading={loading}
        listboxProps={{
          customProps: {
            handleClickIncluded,
            handleClickExcluded,
            handleEdit: onEdit,
            includedValue,
            excludedValue,
          },
        }}
        customList={CustomListForAutocomplete}
        renderTags={(value, getTagProps, ownerState) => {
          return value.map((option: any, index: number) => {
            const { key, ...restTagProps } = getTagProps({ index: index });
            const data = itemList?.find((item) => item.value === option);
            return (
              <RenderTags
                key={key}
                option={data}
                handleDelete={handleDeleteTag}
                color={
                  checkIfValueExist(includedValue, option) ? "success" : "error"
                }
                {...restTagProps}
              />
            );
          });
        }}
      />
    </>
  );
}

interface CustomAutocompleteWithListButtonsProps extends TCustomAutocomplete {
  primaryName: string;
  secondaryName: string;
  name?: string;
  parentControl: Control<any>;
  handleEdit?: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number
  ) => void;
}

function RenderTags({ option, onDelete, handleDelete, color, ...rest }: any) {
  return (
    <Chip
      label={option?.label}
      {...rest}
      onDelete={(e) => handleDelete(e, option, onDelete)}
      color={color}
    />
  );
}

function filterValue(
  data: any[],
  value: Record<string, string | number>,
  onChange: (...event: any[]) => void
) {
  if (checkIfValueExist(data, value.value)) {
    onChange(customFilter(data, value.value));
  }
}

function checkIfValueExist(data: any[] = [], value: string | number) {
  return data.some((element) => element === value);
}

function customFilter(data: any[] = [], value: string | number) {
  return data.filter((element) => element !== value);
}
